import { Dispatch, SetStateAction, useState } from "react"
import { Stack } from "@mui/material"
import { ConfigQuery, fetchResourceFilters, ResourceFiltersResponse } from "../../../createOrderApi"
import {
    ResourceFilterElementValue,
    ResourceFiltersSelectedItem,
    ResourceFiltersTypes,
} from "../../../createOrderTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { AddFiltersButton } from "./AddFiltersButton"
import { FilterChip } from "./FilterChip"

const groupKeys = ["key_group_1", "key_group_2", "key_group_3", "key_group_4", "key_group_5"]

const transformFilters = (data: ResourceFiltersResponse, isTimeline: boolean) => {
    return groupKeys.flatMap((key, index) => {
        const group = data.filterGroups.find((el) => el.translationKey === key)
        if (!group || (isTimeline && (key === "key_group_2" || key === "key_group_3"))) return []

        const elements = group.filterElements ?? []

        return index === 0 // group1
            ? elements.map((el) => ({ ...el, name: group.name }))
            : elements
    })
}

type ResourceFilterMenuProps = Readonly<{
    filters: ResourceFiltersSelectedItem[]
    setFilters: Dispatch<SetStateAction<ResourceFiltersTypes>>
    isTimeline?: boolean
    userId: number
}>

export function ResourceFilterMenu({ filters, userId, setFilters, isTimeline = false }: ResourceFilterMenuProps) {
    const { data: resourceFiltersResult = [] } = useApiQuery(fetchResourceFilters, {
        queryName: ConfigQuery.RESOURCE_FILTERS,
        dependencies: {
            userId,
        },
        select: (data) => transformFilters(data, isTimeline),
    })

    const [activeFilter, setActiveFilter] = useState<string | null>(null)

    const handleAddFilter = (filter: string) => {
        setFilters((prev) => {
            if (prev.filters.some((f) => f.identifier === filter)) {
                return { ...prev, filters: prev.filters.filter((f) => f.identifier !== filter) }
            }
            return { ...prev, filters: [...prev.filters, { identifier: filter, values: [] }] }
        })
        setActiveFilter(filter)
    }

    const updateFilterValues = (identifier: string, values: ResourceFilterElementValue[]) => {
        setFilters((prev) => {
            return {
                ...prev,
                filters: prev.filters.map((filter) =>
                    filter.identifier === identifier ? { ...filter, values } : filter,
                ),
            }
        })
    }

    return (
        <Stack flexDirection="row" gap=".75rem" flexWrap="wrap" width="fit-content" alignItems="center" flex="none">
            {filters.map((filter) => (
                <FilterChip
                    key={filter.identifier}
                    filterType={filter.identifier}
                    isRemovable={filter?.isRemovable}
                    onRemove={() =>
                        setFilters((prev) => {
                            return { ...prev, filters: prev.filters.filter((f) => f !== filter) }
                        })
                    }
                    resourceFiltersResult={resourceFiltersResult}
                    isActive={activeFilter === filter.identifier}
                    setActiveFilter={setActiveFilter}
                    initialValues={filter.values}
                    onSave={(values) => updateFilterValues(filter.identifier, values)}
                />
            ))}

            <AddFiltersButton
                filters={filters}
                resourceFiltersResult={resourceFiltersResult}
                onAddFilter={handleAddFilter}
            />
        </Stack>
    )
}
